import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../components/ui/command";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  Upload,
  Building2,
  Globe,
  Share2,
  DollarSign,
  Phone,
  Clock,
  Sun,
  Moon,
  Laptop,
  Palette,
  Check,
} from "lucide-react";
import { getLocalTimezone } from "../lib/timezone";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { useTheme, colorPresets } from "../contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TIMEZONES = Intl.supportedValuesOf("timeZone");

// Full currency list with symbols
const CURRENCIES = [
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { code: "LKR", symbol: "Rs", name: "Sri Lankan Rupee" },
  { code: "NPR", symbol: "Rs", name: "Nepalese Rupee" },
  { code: "BDT", symbol: "৳", name: "Bangladeshi Taka" },
  { code: "PKR", symbol: "Rs", name: "Pakistani Rupee" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "BRL", symbol: "R$", name: "Brazilian Real" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
];

export default function Settings() {
  const { organization, setOrganization } = useAuth();
  const { theme, setTheme, colorPreset, setColorPreset } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "gym";

  const [form, setForm] = useState({
    name: organization?.name || "",
    email: organization?.email || "",
    phone: organization?.phone || "",
    website: organization?.website || "",
    logo: organization?.logo || "",
    description: organization?.description || "",
    location: organization?.location || "",
    whatsapp: organization?.whatsapp || "",
    facebook: organization?.facebook || "",
    instagram: organization?.instagram || "",
    twitter: organization?.twitter || "",
    currency: organization?.currency || "INR",
    timezone: organization?.timezone || getLocalTimezone(),
    forceCheckinToSeeWorkouts: organization?.forceCheckinToSeeWorkouts || false,
    forceMembersLoginPage: organization?.forceMembersLoginPage || false,
    blockExpiredMembers: organization?.blockExpiredMembers || false,
    blockExpiredMembersGracePeriod: organization?.blockExpiredMembersGracePeriod || 0,
    allowMembersToEditGoals: organization?.allowMembersToEditGoals || false,
    allowMembersToAddProgress: organization?.allowMembersToAddProgress || false,
    allowMemberSelfWorkout: organization?.allowMemberSelfWorkout || false,
    forcePhotoFromCameraOnly: organization?.forcePhotoFromCameraOnly || false,
    theme: organization?.theme || "system",
    colorPreset: organization?.colorPreset || "electric",
    blockNonAndExpiredQrCheckin: organization?.blockNonAndExpiredQrCheckin || false,
    showBiometricSyncOnMembersList: organization?.showBiometricSyncOnMembersList || false,
    blockZeroMembership: organization?.blockZeroMembership || false,
  });

  useEffect(() => {
    if (organization) {
      setForm((prev) => ({
        ...prev,
        name: organization.name || "",
        email: organization.email || "",
        phone: organization.phone || "",
        website: organization.website || "",
        logo: organization.logo || "",
        description: organization.description || "",
        location: organization.location || "",
        whatsapp: organization.whatsapp || "",
        facebook: organization.facebook || "",
        instagram: organization.instagram || "",
        twitter: organization.twitter || "",
        currency: organization.currency || "INR",
        timezone: organization.timezone || getLocalTimezone(),
        forceCheckinToSeeWorkouts:
          organization.forceCheckinToSeeWorkouts || false,
        forceMembersLoginPage:
          organization.forceMembersLoginPage || false,
        blockExpiredMembers:
          organization.blockExpiredMembers || false,
        blockExpiredMembersGracePeriod:
          organization.blockExpiredMembersGracePeriod || 0,
        allowMembersToEditGoals:
          organization.allowMembersToEditGoals || false,
        allowMembersToAddProgress:
          organization.allowMembersToAddProgress || false,
        allowMemberSelfWorkout:
          organization.allowMemberSelfWorkout || false,
        forcePhotoFromCameraOnly:
          organization.forcePhotoFromCameraOnly || false,
        theme:
          organization.theme || "system",
        colorPreset:
          organization.colorPreset || "electric",
        blockNonAndExpiredQrCheckin:
          organization.blockNonAndExpiredQrCheckin || false,
        showBiometricSyncOnMembersList:
          organization.showBiometricSyncOnMembersList || false,
        blockZeroMembership:
          organization.blockZeroMembership || false,
      }));
    }
  }, [organization]);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [timezoneOpen, setTimezoneOpen] = useState(false);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isHeic =
      file.name?.toLowerCase().endsWith(".heic") ||
      file.name?.toLowerCase().endsWith(".heif") ||
      file.type === "image/heic" ||
      file.type === "image/heif";
    if (!isHeic) {
      setLogoPreview(URL.createObjectURL(file));
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("logo", res.data.url);
      setLogoPreview(`${BACKEND_URL}${res.data.url}`);
      toast.success("Logo uploaded");
    } catch {
      toast.error("Upload failed");
      if (!isHeic) setLogoPreview(null);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await api.put("/settings/organization", form);
      setOrganization(res.data.organization);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const selectedCurrency =
    CURRENCIES.find((c) => c.code === form.currency) || CURRENCIES[0];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight">
        Settings
      </h1>

      <Tabs
        value={activeTab}
        onValueChange={(val) => setSearchParams({ tab: val })}
        className="w-full"
      >
        <TabsList className="grid w-full max-w-[600px] grid-cols-3 mb-6">
          <TabsTrigger value="gym" data-testid="tab-gym">
            Gym Setting
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            Members
          </TabsTrigger>
          <TabsTrigger value="app" data-testid="tab-app">
            App Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="gym" className="space-y-6">
          {/* ── Gym Details ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Gym Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo */}
              <div className="flex items-center gap-4 mb-2">
                <div className="w-24 h-24 rounded-sm bg-secondary flex items-center justify-center overflow-hidden border border-border">
                  {logoPreview || form.logo ? (
                    <img
                      src={logoPreview || `${BACKEND_URL}${form.logo}`}
                      alt="Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="logo"
                    className="cursor-pointer text-primary text-sm hover:underline"
                  >
                    Upload Logo
                  </Label>
                  <input
                    id="logo"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogo}
                    data-testid="logo-upload"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 200×200px
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Gym Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="rounded-sm mt-1"
                    data-testid="gym-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="rounded-sm mt-1"
                    data-testid="gym-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="https://yourgym.com"
                  />
                </div>
              </div>

              <div>
                <Label>Location / Address</Label>
                <Textarea
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  className="rounded-sm mt-1"
                  rows={3}
                  placeholder="123, Main Street, City, State - Pincode"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  className="rounded-sm mt-1"
                  rows={3}
                  placeholder="Tell members about your gym..."
                />
              </div>
            </CardContent>
          </Card>

          {/* ── Currency & Timezone ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Regional Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Currency</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => update("currency", v)}
                  >
                    <SelectTrigger className="rounded-sm mt-1">
                      <SelectValue>
                        <span>
                          {selectedCurrency.symbol} — {selectedCurrency.code} (
                          {selectedCurrency.name})
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="font-mono mr-2 text-primary">
                            {c.symbol}
                          </span>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Currently using:{" "}
                    <span className="font-semibold text-primary">
                      {selectedCurrency.symbol}
                    </span>{" "}
                    ({selectedCurrency.code})
                  </p>
                </div>

                <div>
                  <Label>Timezone</Label>
                  <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={timezoneOpen}
                        className="w-full justify-between rounded-sm mt-1 font-normal font-sans border-input bg-background"
                      >
                        {form.timezone
                          ? form.timezone.replace(/_/g, " ")
                          : "Select timezone..."}
                        <Clock className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[var(--radix-popover-trigger-width)] p-0"
                      align="start"
                    >
                      <Command>
                        <CommandInput
                          className="outline-none focus:border-0 focus:ring-0 rounded-none"
                          placeholder="Search timezone..."
                        />
                        <CommandList>
                          <CommandEmpty>No timezone found.</CommandEmpty>
                          <CommandGroup className="max-h-60 overflow-y-auto">
                            {TIMEZONES.map((tz) => (
                              <CommandItem
                                key={tz}
                                value={tz}
                                onSelect={() => {
                                  update("timezone", tz);
                                  setTimezoneOpen(false);
                                }}
                              >
                                {tz.replace(/_/g, " ")}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for display times across the platform
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── WhatsApp ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Phone className="h-5 w-5 text-primary" />
                WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>WhatsApp Number</Label>
                  <Input
                    value={form.whatsapp}
                    onChange={(e) => update("whatsapp", e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="+91 98765 43210"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used for member communication and quick links
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Social Media ── */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Share2 className="h-5 w-5 text-primary" />
                Social Media
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Facebook</Label>
                  <Input
                    value={form.facebook}
                    onChange={(e) => update("facebook", e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="https://facebook.com/yourgym"
                  />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input
                    value={form.instagram}
                    onChange={(e) => update("instagram", e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="https://instagram.com/yourgym"
                  />
                </div>
                <div>
                  <Label>X (Twitter)</Label>
                  <Input
                    value={form.twitter}
                    onChange={(e) => update("twitter", e.target.value)}
                    className="rounded-sm mt-1"
                    placeholder="https://x.com/yourgym"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-6">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                Member Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Force Workout Check-in
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Force the member to check in to see the workouts
                  </p>
                </div>
                <Switch
                  checked={form.forceCheckinToSeeWorkouts}
                  onCheckedChange={(checked) =>
                    update("forceCheckinToSeeWorkouts", checked)
                  }
                  data-testid="force-checkin-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Force Camera Photo
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Force the user to take a live photo using their camera (no gallery selection) during signup
                  </p>
                </div>
                <Switch
                  checked={form.forcePhotoFromCameraOnly}
                  onCheckedChange={(checked) =>
                    update("forcePhotoFromCameraOnly", checked)
                  }
                  data-testid="force-photo-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Force Member Login Page
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Hide staff login by default and show Member Login only
                  </p>
                </div>
                <Switch
                  checked={form.forceMembersLoginPage}
                  onCheckedChange={(checked) =>
                    update("forceMembersLoginPage", checked)
                  }
                  data-testid="force-member-login-toggle"
                />
              </div>
              <div className="flex flex-col gap-4 p-4 rounded-md border border-border bg-secondary/10">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-semibold">
                      Block Workouts for Expired Members
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Prevent members with expired or missing memberships from viewing workouts
                    </p>
                  </div>
                  <Switch
                    checked={form.blockExpiredMembers}
                    onCheckedChange={(checked) =>
                      update("blockExpiredMembers", checked)
                    }
                    data-testid="block-expired-toggle"
                  />
                </div>
                 {form.blockExpiredMembers && (
                  <div className="flex flex-col gap-1.5 pl-1 max-w-[200px]" data-testid="grace-period-container">
                    <Label htmlFor="blockExpiredMembersGracePeriod" className="text-sm">
                      Grace Period (Days)
                    </Label>
                    <Input
                      id="blockExpiredMembersGracePeriod"
                      type="number"
                      min="0"
                      value={form.blockExpiredMembersGracePeriod}
                      onChange={(e) => update("blockExpiredMembersGracePeriod", parseInt(e.target.value) || 0)}
                      className="rounded-sm"
                      data-testid="grace-period-input"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Block Non-members & Expired Members QR Check-in
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent non-members and members with expired memberships from checking in via QR code (By default, allowed with warning dialog)
                  </p>
                </div>
                <Switch
                  checked={form.blockNonAndExpiredQrCheckin}
                  onCheckedChange={(checked) =>
                    update("blockNonAndExpiredQrCheckin", checked)
                  }
                  data-testid="block-non-expired-qr-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Show Biometric Turnstile Sync on Members List
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Display turnstile access status badges and manual sync button inside member details accordion (only shown when an active biometric device is connected)
                  </p>
                </div>
                <Switch
                  checked={form.showBiometricSyncOnMembersList}
                  onCheckedChange={(checked) =>
                    update("showBiometricSyncOnMembersList", checked)
                  }
                  data-testid="show-biometric-sync-members-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Block Biometric Access for Members with Zero Memberships
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically block turnstile / biometric gate access (sets machine validity to 2000-01-01) for members who do not have any registered memberships. Disabled by default
                  </p>
                </div>
                <Switch
                  checked={form.blockZeroMembership}
                  onCheckedChange={(checked) =>
                    update("blockZeroMembership", checked)
                  }
                  data-testid="block-zero-membership-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Allow members to edit goals
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enable members to modify their fitness goals and targets from the member portal
                  </p>
                </div>
                <Switch
                  checked={form.allowMembersToEditGoals}
                  onCheckedChange={(checked) =>
                    update("allowMembersToEditGoals", checked)
                  }
                  data-testid="allow-edit-goals-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Allow members to add progress
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enable members to log and edit their own progress measurements globally
                  </p>
                </div>
                <Switch
                  checked={form.allowMembersToAddProgress}
                  onCheckedChange={(checked) =>
                    update("allowMembersToAddProgress", checked)
                  }
                  data-testid="allow-add-progress-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-md border border-border bg-secondary/10">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">
                    Allow members to add self workout
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enable members to create and log their own self workouts globally
                  </p>
                </div>
                <Switch
                  checked={form.allowMemberSelfWorkout}
                  onCheckedChange={(checked) =>
                    update("allowMemberSelfWorkout", checked)
                  }
                  data-testid="allow-member-self-workout-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="app" className="space-y-6">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                App Appearance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Mode */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Theme Mode</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Choose how the gym platform looks on your device
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "light", label: "Light Mode", icon: Sun },
                    { value: "dark", label: "Dark Mode", icon: Moon },
                    { value: "system", label: "System Mode", icon: Laptop },
                  ].map((mode) => {
                    const ModeIcon = mode.icon;
                    const isActive = form.theme === mode.value;
                    return (
                      <Button
                        key={mode.value}
                        type="button"
                        variant={isActive ? "default" : "outline"}
                        className={`flex flex-col items-center justify-center h-20 gap-2 transition-all rounded-sm ${
                          isActive
                            ? "border-primary bg-primary text-primary-foreground shadow-sm scale-[1.02]"
                            : "hover:border-primary/50 hover:bg-secondary/40"
                        }`}
                        onClick={() => {
                          setTheme(mode.value);
                          update("theme", mode.value);
                        }}
                      >
                        <ModeIcon className="h-5 w-5" />
                        <span className="text-xs font-semibold">{mode.label}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Color Presets */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Accent Color</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Choose the primary highlight color for buttons, badges, and active elements
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(colorPresets).map(([key, value]) => {
                    const isActive = form.colorPreset === key;
                    const bgClass =
                      key === "electric"
                        ? "bg-red-500"
                        : key === "ocean"
                          ? "bg-blue-500"
                          : key === "onex"
                            ? "bg-[#019444]"
                            : "bg-emerald-500";
                    return (
                      <Button
                        key={key}
                        type="button"
                        variant={isActive ? "default" : "outline"}
                        className={`flex flex-col items-center justify-center h-24 gap-2 transition-all rounded-sm ${
                          isActive
                            ? "border-primary bg-secondary/80 text-foreground shadow-md scale-[1.02] border-2"
                            : "hover:border-primary/50 hover:bg-secondary/40"
                        }`}
                        onClick={() => {
                          setColorPreset(key);
                          update("colorPreset", key);
                        }}
                      >
                        <div className={`h-6 w-6 rounded-full ${bgClass} shadow-inner flex items-center justify-center`}>
                          {isActive && <Check className="h-3.5 w-3.5 text-white" />}
                        </div>
                        <span className="text-xs font-semibold">{value.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Button
        onClick={handleSave}
        disabled={loading}
        className="rounded-sm w-full md:w-auto"
        data-testid="save-settings-btn"
      >
        {loading ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}
